import React from 'react';

import {
  Box,
  Badge,
  Grid,
  Avatar,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';
import { useGetIdentity } from 'react-admin';

const Footer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.customSidebar.footer.background,
  padding: `${theme.spacing(2.75)} ${theme.spacing(4)}`,
  borderRight: '1px solid rgba(0, 0, 0, 0.12)',
}));

const CollapsedFooter = styled(Footer)({
  display: 'flex',
  justifyContent: 'center',
  paddingLeft: 0,
  paddingRight: 0,
  '& .MuiBadge-root': {
    marginRight: 0,
  },
});

const FocusableAvatar = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  borderRadius: '50%',
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.common.white}`,
    outlineOffset: 2,
  },
}));

const FooterText = styled(Typography)(({ theme }) => ({
  color: theme.customSidebar.footer.color,
}));

const FooterSubText = styled(Typography)(({ theme }) => ({
  color: theme.customSidebar.footer.color,
  fontSize: '0.7rem',
  display: 'block',
  padding: '1px',
}));

const FooterBadge = styled(Badge)(({ theme }) => ({
  marginRight: theme.spacing(1),
  '& span': {
    backgroundColor: theme.customSidebar.footer.online.background,
    border: `1.5px solid ${theme.palette.common.white}`,
    height: '12px',
    width: '12px',
    borderRadius: '50%',
  },
}));

const SidebarFooter = ({ collapsed = false }: { collapsed?: boolean }) => {
  const { identity } = useGetIdentity();

  const isLoaded = identity;

  const avatar = (
    <FooterBadge
      overlap="circular"
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      variant="dot"
    >
      {isLoaded && <Avatar alt={identity.displayName} src={identity.avatar} />}
    </FooterBadge>
  );

  if (collapsed) {
    return (
      <CollapsedFooter>
        {isLoaded ? (
          <Tooltip
            placement="right"
            title={(
              <>
                <Typography variant="body2">{identity.displayName}</Typography>
                <Typography variant="caption" display="block">{identity.accountName}</Typography>
              </>
            )}
          >
            <FocusableAvatar tabIndex={0}>
              {avatar}
            </FocusableAvatar>
          </Tooltip>
        ) : avatar}
      </CollapsedFooter>
    );
  }

  return (
    <Footer>
      <Grid container spacing={2}>
        <Grid>
          {avatar}
        </Grid>
        <Grid>
          {isLoaded && (
            <>
              <FooterText variant="body2">{identity.displayName}</FooterText>
              <FooterSubText variant="caption">{identity.accountName}</FooterSubText>
            </>
          )}
        </Grid>
      </Grid>
    </Footer>
  );
};

export default SidebarFooter;
